import { setOfElements, noop, isDefined, valueProvider, lazyValue } from '@proc7ts/primitives';
import { afterEventBy, sendEventsTo, trackValue, mapAfter_, AfterEvent__symbol, supplyAfter, onEventBy } from '@proc7ts/fun-events';
import { itsElements, valueIt } from '@proc7ts/push-iterator';
import { neverSupply, Supply } from '@proc7ts/supply';

/**
 * An error indicating context module dependency load failure.
 */
class CxDependencyError extends ReferenceError {
    /**
     * Constructs context module dependency load error.
     *
     * @param module - A module failed to load.
     * @param reasons - An array of dependency/reason pairs caused the load failure.
     * @param message - An error message.
     */
    constructor(module, reasons = [], message = CxDependencyError$defaultMessage(module, reasons)) {
        super(message);
        this.module = module;
        this.reasons = reasons;
    }
    get name() {
        return 'CxDependencyError';
    }
}
function CxDependencyError$defaultMessage(module, dependencies) {
    const reasons = dependencies.reduce((out, [dep, reason]) => {
        if (out) {
            out += ', ';
        }
        else {
            out = ': ';
        }
        if (reason !== undefined) {
            out += `${dep} failed to load (${reason})`;
        }
        else {
            out += `${dep} not loaded`;
        }
        return out;
    }, '');
    return `Failed to load ${module}${reasons}`;
}

const CxModule$Impl__symbol = ( /*#__PURE__*/Symbol('CxModule.impl'));
class CxModule$Impl {
    constructor(module, name, options) {
        this.module = module;
        this.name = name;
        this.options = options;
        const { needs, has, setup } = options;
        this.has = setOfElements(has).add(module);
        this.needs = setOfElements(needs);
        this._setup = setup ? setup.bind(options) : noop;
    }
    async setup(setup) {
        const deps = CxModule$deps(setup);
        // Await for module dependencies to be settled.
        if (!await CxModule$loadDeps(setup, deps, CxModule$whenSettled)) {
            return;
        }
        setup.initBy(async () => {
            // Initialize module dependencies.
            await CxModule$loadDeps(setup, deps, CxModule$whenReady);
        });
        await this._setup(setup);
    }
}
function CxModule$replace(replaced, replacement) {
    return {
        entry: replaced,
        placeAsset(_target, collector) {
            collector(replacement);
        },
    };
}
function CxModule$implement(target) {
    const module = target.entry;
    return afterEventBy(receiver => {
        const receive = sendEventsTo(receiver);
        target.trackAssetList(candidates => {
            let impl;
            for (let i = candidates.length - 1; i >= 0; --i) {
                const recent = candidates[i].recentAsset;
                if (recent) {
                    impl = recent.asset;
                    if (impl !== module) {
                        break;
                    }
                }
            }
            receive(impl);
        }, receiver.supply);
    });
}
function CxModule$deps(setup) {
    const { module, supply } = setup;
    return itsElements(valueIt(module.needs, dep => dep !== module
        && setup.provide(dep).needs(supply)
        && {
            dep,
            use: setup.get(dep).use(setup),
        }));
}
function CxModule$loadDeps(setup, deps, whenLoaded) {
    const { module, supply } = setup;
    const notLoaded = valueProvider(false);
    const whenDone = supply.whenDone().then(notLoaded, notLoaded);
    return Promise.race([
        whenDone,
        Promise
            .all(deps
            .map(({ dep, use }) => whenLoaded(use).then(noop, error => [dep, error])))
            .then((results) => {
            const failures = results.filter(isDefined);
            return failures.length
                ? new CxDependencyError(module, failures) // Prevent unhandled promise rejection
                : true;
        }),
    ]).then(result => {
        if (typeof result !== 'boolean') {
            // Fail to load module if at leas one of its dependencies failed.
            return Promise.reject(result);
        }
        return result;
    });
}
function CxModule$whenSettled(use) {
    return use.whenSettled;
}
function CxModule$whenReady(use) {
    return use.whenReady;
}

class CxModule$Usage {
    constructor(target, module) {
        this.module = module;
        this._useCounter = 0;
        this._impl = trackValue();
        this._rev = trackValue({
            status: {
                module: this.module,
                provided: false,
                used: false,
                settled: false,
                ready: false,
            },
            supply: neverSupply(),
        });
        const supply = target.supply;
        supply.cuts(this._impl);
        supply.cuts(this._rev);
        this._impl.read(module => {
            const prevSupply = this._rev.it.supply;
            if (module) {
                this._load(module);
            }
            prevSupply.off();
        });
    }
    createHandle() {
        const read = this._rev.read.do(mapAfter_(({ status }) => status));
        const handle = {
            read,
            [AfterEvent__symbol]: valueProvider(read),
            use: (user) => this._use(handle, user),
        };
        return handle;
    }
    setup(target) {
        this._setup = () => {
            const rev = this._rev.it;
            const { status: { module }, supply } = rev;
            if (module !== this.module) {
                // Load implementation module instead.
                // The implementation module expected to be provided already.
                target.get(module).use(supply).read({
                    supply,
                    receive: (_ctx, { settled, ready, error }) => {
                        this._updateStatus(rev, settled, ready, error);
                    },
                });
            }
            else {
                CxModule$load(target, rev)
                    .then(({ whenReady }) => {
                    this._updateStatus(rev, true, false);
                    return whenReady;
                })
                    .then(() => this._updateStatus(rev, true, true))
                    .catch(error => rev.supply.off(error));
            }
        };
    }
    implementBy(impl) {
        this._impl.by(impl);
    }
    _updateStatus(rev, settled, ready, error) {
        // Ensure updating the correct revision.
        if (this._rev.it.supply !== rev.supply) {
            // If revision changed, then drop the obsolete one.
            rev.supply.off();
        }
        else {
            this._rev.it = rev = {
                status: {
                    module: rev.status.module,
                    provided: rev.status.provided,
                    used: true,
                    settled,
                    ready,
                    error,
                },
                supply: rev.supply,
            };
        }
    }
    _load(module) {
        const supply = new Supply(noop).needs(this._rev).whenOff(error => {
            const rev = this._rev.it;
            if (rev.supply === supply) {
                this._rev.it = {
                    status: {
                        ...this._rev.it.status,
                        provided: false,
                        settled: false,
                        ready: false,
                        error,
                    },
                    supply,
                };
            }
        });
        const used = !!this._useCounter;
        this._rev.it = {
            status: {
                module,
                provided: true,
                used,
                settled: false,
                ready: false,
            },
            supply,
        };
        if (used) {
            this._setup();
        }
    }
    _use(handle, user) {
        const supply = new Supply(noop);
        if (user) {
            supply.needs(user);
        }
        const read = handle.read.do(supplyAfter(supply));
        const use = {
            ...handle,
            read,
            whenSettled: CxModule$Use$when(read, CxModule$isSettled),
            whenReady: CxModule$Use$when(read, CxModule$isReady),
            supply,
        };
        if (!supply.isOff) {
            supply.whenOff(error => {
                if (!--this._useCounter) {
                    const rev = this._rev.it;
                    this._rev.it = {
                        status: {
                            ...rev.status,
                            used: false,
                            settled: false,
                            ready: false,
                            error,
                        },
                        supply: new Supply(noop).off(error),
                    };
                    rev.supply.off(error);
                }
            });
            if (!this._useCounter++) {
                // Mark the module used and set it up.
                const rev = this._rev.it;
                this._rev.it = {
                    status: {
                        ...rev.status,
                        used: true,
                    },
                    supply: rev.supply,
                };
                this._setup();
            }
        }
        return use;
    }
}
async function CxModule$load(target, { status: { module }, supply }) {
    const moduleInit = new CxModule$Init(module);
    await module.setup({
        context: target.context,
        module,
        supply,
        get(entry, request) {
            return target.get(entry, request);
        },
        provide(asset) {
            return target.provide(asset).needs(supply);
        },
        initBy(init) {
            moduleInit.initBy(init);
        },
    });
    return moduleInit;
}
class CxModule$Init {
    constructor(_module) {
        this._module = _module;
        this._whenDone = Promise.resolve();
        this.whenReady = new Promise(resolve => this._ready = resolve);
    }
    initBy(init) {
        const rev = this._whenDone = this._whenDone
            .then(init)
            .finally(() => this._done(rev));
    }
    _done(rev) {
        if (this._whenDone === rev) {
            this._ready(rev);
            this.initBy = _init => {
                throw new TypeError(`${this._module} initialized already, and does not accept new initializers`);
            };
        }
    }
}
function CxModule$Use$when(status, test) {
    return onEventBy(receiver => status({
        supply: receiver.supply,
        receive: (context, status) => {
            if (test(status)) {
                receiver.receive(context, status);
                receiver.supply.off();
            }
            else if (status.error) {
                receiver.supply.off(status.error);
            }
        },
    }));
}
function CxModule$isSettled({ settled }) {
    return settled;
}
function CxModule$isReady({ ready }) {
    return ready;
}

/**
 * Context module.
 *
 * Modules intended to extend the context dynamically.
 *
 * The module is a context entry that can be used to provide module instance and access its
 * {@link CxModule.Handle handle}.
 *
 * Usage example:
 * ```typescript
 * // Construct new module.
 * const myModule = new CxModule('my module', {
 *   setup(setup) {
 *     // Provide the values
 *     setup.provide({ a: Foo, is: 'foo' });
 *   },
 * });
 *
 * // Load the module
 * const myModuleSupply = contextBuilder.provide(myModule);
 *
 * // Start using the module
 * const myModuleUse = await context.get(myModule).use();
 *
 * // Await for the module to load
 * await myModuleUse.whenReady;
 *
 * // Access the value provided by module.
 * console.log(context.get(Foo));
 *
 * // Stop using the module
 * myModuleUse.supply.off();
 *
 * // Unload the module declaration.
 * myModuleSupply.off();
 * ```
 */
class CxModule {
    /**
     * Constructs context module.
     *
     * @param name - Human-readable module name.
     * @param options - Module construction options.
     */
    constructor(name, options = {}) {
        this[CxModule$Impl__symbol] = new CxModule$Impl(this, name, options);
    }
    /**
     * Human-readable module name.
     */
    get name() {
        return this[CxModule$Impl__symbol].name;
    }
    get [Symbol.toStringTag]() {
        return this.name;
    }
    get entry() {
        return this;
    }
    /**
     * The modules this one requires.
     *
     * Assigned by {@link CxModule.Options.needs} option.
     */
    get needs() {
        return this[CxModule$Impl__symbol].needs;
    }
    /**
     * The modules this one provides.
     *
     * Assigned by {@link CxModule.Options.has} option.
     *
     * Always contains the module itself.
     */
    get has() {
        return this[CxModule$Impl__symbol].has;
    }
    perContext(target) {
        const getUsageAndHandle = lazyValue(() => {
            const usage = new CxModule$Usage(target, this);
            return [usage, usage.createHandle()];
        });
        let getHandle = () => {
            const [usage, handle] = getUsageAndHandle();
            getHandle = valueProvider(handle); // Allow to receive handle before setup completes.
            // Set up only once.
            usage.setup(target);
            usage.implementBy(CxModule$implement(target));
            return handle;
        };
        return {
            assign(receiver) {
                receiver(getHandle());
            },
        };
    }
    placeAsset(_target, collector) {
        collector(this);
    }
    setupAsset(target) {
        for (const replaced of this.has) {
            if (replaced !== this && replaced !== target.entry) {
                target.provide(CxModule$replace(replaced, this));
            }
        }
    }
    /**
     * Sets up the module.
     *
     * This method is called when loading the module. It is used e.g. to provide more values for the context.
     *
     * By default:
     *
     * 1. Satisfies module {@link needs dependencies} by setting them up.
     *
     *    The dependency considered satisfied when it is {@link CxModule.Status.settled settled}.
     *
     * 2. {@link CxModule.Setup.initBy Initializes} the module by initializing the dependencies.
     *
     *    The dependency considered initialized when it is {@link CxModule.Status.ready ready for use}.
     *
     * 3. Performs the module setup by invoking the {@link CxModule.Options.setup} method.
     *
     * @param setup - Context module setup.
     *
     * @returns A promise resolved when the module is set up asynchronously.
     */
    setup(setup) {
        return this[CxModule$Impl__symbol].setup(setup);
    }
    toString() {
        return `[CxModule ${this[Symbol.toStringTag]}]`;
    }
}

export { CxDependencyError, CxModule };
//# sourceMappingURL=context-modules.js.map
